import { useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button, Field, Input, Modal } from '@/ui/primitives';
import { Stack } from '@/ui/layout';
import { ApiError } from '@/lib/api-client';
import {
  imovelCreateSchema,
  imovelUpdateSchema,
  type ImovelCreateInput,
  type ImovelResumo,
  type ImovelUpdateInput,
} from '../schemas';
import { useCreateImovel, useUpdateImovel } from '../api';

type Mode =
  | { kind: 'create'; cpfDono: string }
  | { kind: 'edit'; cpfDono: string; imovel: ImovelResumo };

interface Props {
  mode: Mode | null;
  onClose: () => void;
}

export function ImovelEditModal({ mode, onClose }: Props) {
  const create = useCreateImovel(mode?.cpfDono ?? null);
  const update = useUpdateImovel(mode?.cpfDono ?? null);

  const isEdit = mode?.kind === 'edit';
  const editing = isEdit ? mode.imovel : null;

  const form = useForm<ImovelCreateInput>({
    resolver: zodResolver(isEdit ? imovelUpdateSchema : imovelCreateSchema),
    defaultValues: {
      proprietario_cpf: mode?.cpfDono ?? '',
      denominacao: editing?.denominacao ?? '',
      matricula: editing?.matricula ?? '',
      municipio: editing?.municipio ?? '',
      uf: editing?.uf ?? '',
      area_total_ha: editing?.area_total_ha ?? null,
      car_codigo: editing?.car_codigo ?? '',
    },
  });

  useEffect(() => {
    if (mode) {
      const im = mode.kind === 'edit' ? mode.imovel : null;
      form.reset({
        proprietario_cpf: mode.cpfDono,
        denominacao: im?.denominacao ?? '',
        matricula: im?.matricula ?? '',
        municipio: im?.municipio ?? '',
        uf: im?.uf ?? '',
        area_total_ha: im?.area_total_ha ?? null,
        car_codigo: im?.car_codigo ?? '',
      });
    }
  }, [mode, form]);

  function onSubmit() {
    if (!mode) return;
    if (mode.kind === 'create') {
      void form.handleSubmit((values) =>
        create.mutate(values, { onSuccess: onClose }),
      )();
    } else {
      void form.handleSubmit((values) => {
        const patch: ImovelUpdateInput = {
          denominacao: values.denominacao,
          matricula: values.matricula,
          municipio: values.municipio,
          uf: values.uf,
          ...(values.area_total_ha != null && { area_total_ha: values.area_total_ha }),
          car_codigo: values.car_codigo,
        };
        update.mutate(
          { id: mode.imovel.id, patch },
          { onSuccess: onClose },
        );
      })();
    }
  }

  const pending = create.isPending || update.isPending;
  const error = create.error ?? update.error;

  return (
    <Modal
      open={mode !== null}
      onOpenChange={(v) => !v && onClose()}
      title={isEdit ? `Editar — ${editing?.denominacao ?? 'imóvel'}` : 'Novo imóvel'}
      size="lg"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button variant="primary" onClick={onSubmit} disabled={pending}>
            {pending ? 'Salvando…' : isEdit ? 'Salvar' : 'Criar imóvel'}
          </Button>
        </>
      }
    >
      <form onSubmit={(e) => e.preventDefault()}>
        <Stack gap="md">
          <Field
            label="Denominação"
            htmlFor="im-deno"
            error={form.formState.errors.denominacao?.message}
            required
          >
            <Input id="im-deno" {...form.register('denominacao')} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Matrícula" htmlFor="im-mat">
              <Input id="im-mat" {...form.register('matricula')} />
            </Field>
            <Field label="CAR" htmlFor="im-car">
              <Input id="im-car" {...form.register('car_codigo')} />
            </Field>
            <Field label="Município" htmlFor="im-mun">
              <Input id="im-mun" {...form.register('municipio')} />
            </Field>
            <Field label="UF" htmlFor="im-uf">
              <Input id="im-uf" maxLength={2} {...form.register('uf')} />
            </Field>
            <Field label="Área total (ha)" htmlFor="im-area" className="col-span-2">
              <Input
                id="im-area"
                type="number"
                step="0.01"
                {...form.register('area_total_ha', {
                  valueAsNumber: true,
                  setValueAs: (v: unknown) => (v === '' || v == null ? null : Number(v)),
                })}
              />
            </Field>
          </div>
          {error && (
            <p className="text-xs text-danger">
              {error instanceof ApiError ? error.message : 'Falha ao salvar imóvel'}
            </p>
          )}
        </Stack>
      </form>
    </Modal>
  );
}
